/**
 * Module 'auth': people (Sign in with GitHub, Google or Microsoft), roles, and tokens for
 * CLIs and AI agents.
 *
 * Roles, lowest to highest: viewer (reads everything, acts on nothing), operator (runs
 * actions), admin (also manages members, tokens, machines and policies), owner (also
 * grants owner). The hub enforces them on every route and action; the UI only mirrors it.
 */
import type { Id, Ts } from '../index.ts';

export type Role = 'owner' | 'admin' | 'operator' | 'viewer';

export const ROLES: readonly Role[] = ['viewer', 'operator', 'admin', 'owner'];

export const ROLE_RANK: Record<Role, number> = { viewer: 0, operator: 1, admin: 2, owner: 3 };

export const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', operator: 'Operator', viewer: 'Viewer' };

export const ROLE_DESCRIPTION: Record<Role, string> = {
  viewer: 'Sees everything, changes nothing.',
  operator: 'Starts and stops sessions, runs jobs and pipeline actions.',
  admin: 'Also manages members, tokens, machines and policies.',
  owner: 'Everything, including granting the owner role.',
};

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v);
}

/** True when `role` is at least `min`. */
export function roleAtLeast(role: Role, min: Role): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

/** The lower of two roles. */
export function minRole(a: Role, b: Role): Role {
  return ROLE_RANK[a] <= ROLE_RANK[b] ? a : b;
}

/**
 * Token scopes. An empty list means everything the token's role allows. Otherwise a request
 * needs the matching scope: 'read' for any read, 'actions' (or one action kind such as
 * 'session.start') for POST /api/actions, 'write' for other changes, 'admin' for managing
 * members, tokens, machines and policies. The AI API's tier names ('operate', 'elevated')
 * and capability names ('approval.approve') are scopes too: POST /api/ai/invoke gates each
 * capability on them (decideCapability in ./ai.ts), and on 'actions' and 'write'.
 */
export const TOKEN_SCOPES = ['read', 'actions', 'write', 'admin'] as const;
export type TokenScope = (typeof TOKEN_SCOPES)[number];

export const TOKEN_SCOPE_LABEL: Record<TokenScope, string> = {
  read: 'Read state, audit and logs',
  actions: 'Run any action',
  write: 'Other changes (approvals, missions)',
  admin: 'Manage members, tokens and machines',
};

/** API tokens never live longer than this. */
export const TOKEN_MAX_DAYS = 30;

/** GitHub sign-in sessions in the browser last this long. */
export const MEMBER_SESSION_TTL_MS = 7 * 24 * 60 * 60_000;

/** Linking a sign-in, or confirming one an admin added, needs a browser sign-in this recent. */
export const LINK_RECENT_MS = 10 * 60_000;

/** Audit log actions this module writes (beside the machine actions in ACTION_LABEL). Audited on machine 'hub'. */
export const AUTH_AUDIT_LABEL: Record<string, string> = {
  'auth.sign-in': 'Sign in',
  'auth.signup': 'Create workspace',
  'auth.rule.set': 'Allow on sign-in list',
  'auth.rule.remove': 'Remove from sign-in list',
  'auth.member.role': 'Change role',
  'auth.member.remove': 'Remove member',
  'auth.token.create': 'Create API token',
  'auth.token.revoke': 'Revoke API token',
  'auth.session.revoke': 'Sign out a browser',
  'auth.session.revoke-all': 'Sign out every browser',
  'auth.live.cut': 'Close live connections',
  'join-token.mint': 'Create join token',
  'join-token.cancel': 'Cancel join token',
  // The names before WP-16 (18 Oct 2026), so older audit entries still read.
  'machine.join-token.create': 'Create join token',
  'machine.join-token.cancel': 'Cancel join token',
  'machine.enrol': 'Enrol machine',
  'machine.self-revoke': 'Machine uninstalled itself',
  'auth.identity.link': 'Link sign-in',
  'auth.identity.unlink': 'Unlink sign-in',
  'auth.identity.confirm': 'Confirm sign-in',
  'auth.sessions.end': 'Sign out everywhere',
  'auth.invite.create': 'Create invite',
  'auth.invite.revoke': 'Revoke invite',
  'auth.invite.accept': 'Accept invite',
  'auth.join-rule.set': 'Add join rule',
  'auth.join-rule.remove': 'Remove join rule',
  'auth.sign-in.policy': 'Change who may sign in',
  'auth.removed.allow': 'Allow a removed person again',
};

// ---------------------------------------------------------------------------
// Browser sessions, the owner link and join tokens (node-trust P0 items 17 to 19)
// ---------------------------------------------------------------------------

/** The owner link works once, within this long of being minted (SD-6). */
export const OWNER_LINK_TTL_MS = 10 * 60_000;
/** A browser session unused this long ends (SD-6). */
export const SESSION_IDLE_MS = 12 * 60 * 60_000;
/** A browser session ends this long after sign-in, used or not (SD-6). */
export const SESSION_MAX_MS = 7 * 24 * 60 * 60_000;
/** Open browser streams and sockets have their credential checked again this often (SD-2). */
export const LIVE_RECHECK_MS = 30_000;
/** WebSocket close code when the credential behind a socket was revoked (SD-2). */
export const LIVE_REVOKED_CLOSE_CODE = 4401;

/**
 * Why the hub ended a live stream (SSE event `auth-ended`, SD-2): the credential that opened it
 * no longer holds as it did. `role-changed` asks the page to reconnect once under the new role;
 * every other reason means this browser or token is no longer signed in, so the page stops
 * reconnecting and shows the signed-out state.
 */
export type AuthEndReason = 'signed-out' | 'removed' | 'revoked' | 'expired' | 'role-changed';
export const AUTH_END_REASONS: readonly AuthEndReason[] = ['signed-out', 'removed', 'revoked', 'expired', 'role-changed'];

/** The last frame of a stream the hub ends because its credential changed: `event: auth-ended`. */
export interface AuthEndedEvent {
  reason: AuthEndReason;
}

/** One signed-in browser, as GET /api/browser-sessions (owner link) and GET /api/auth/sessions (members) list it. Never the cookie. */
export interface BrowserSessionInfo {
  /** A short public id (not the cookie, not its hash). */
  id: string;
  kind: 'owner-link' | SignInProviderId;
  /** 'owner' for the owner link, else the member's login. */
  login: string;
  /** The browser, from its User-Agent at sign-in, clipped. */
  label: string | null;
  createdAt: Ts;
  lastSeenAt: Ts;
  /** When it ends: the earlier of idle and absolute expiry. */
  expiresAt: Ts;
  /** True for the browser asking. */
  current: boolean;
}

export interface SessionsResponse {
  sessions: BrowserSessionInfo[];
  idleMs: number;
  maxMs: number;
}

/** An outstanding join token, as GET /api/join-tokens lists it. Never the token itself. */
export interface JoinTokenInfo {
  id: Id;
  createdAt: Ts;
  expiresAt: Ts;
  /** Who minted it ('owner', a login, or 'hub' for the hub's own machine). */
  createdBy: string;
  /** Minted for the hub's own in-process agent. */
  local: boolean;
}

export interface JoinTokensResponse {
  tokens: JoinTokenInfo[];
}

/** Prefix of every Orgus API token, so secret scanners and people can recognise one. */
export const API_TOKEN_PREFIX = 'ast_';

// ------------------------------------------------------------------ sign-in providers

/** Every way a person signs in to a hub besides the owner link. */
export type SignInProviderId = 'github' | 'google' | 'microsoft';
/** The OpenID Connect providers (GitHub is plain OAuth). */
export type OidcProviderId = 'google' | 'microsoft';

export const SIGN_IN_PROVIDERS: readonly SignInProviderId[] = ['github', 'google', 'microsoft'];

export const SIGN_IN_PROVIDER_LABEL: Record<SignInProviderId, string> = { github: 'GitHub', google: 'Google', microsoft: 'Microsoft' };

/**
 * The client ids of Orgus's own OAuth apps (public by design; the secrets never live in code).
 * A hub or control plane that has a provider's client secret but no client id uses these.
 */
export const ORGUS_OAUTH_CLIENT_IDS: Readonly<Record<SignInProviderId, string>> = {
  github: 'Ov23liqCTkZ5SGewBmEh',
  google: '450037657223-15ob4gnkvd7siqj5tqmt3qci48iig21u.apps.googleusercontent.com',
  microsoft: '88c21e25-4bb0-42d3-b20f-b6f2757a2919',
};

/** The tenant setting of Orgus's own Microsoft app: work, school and personal accounts. */
export const ORGUS_MICROSOFT_APP_TENANT = 'common';

/** The Microsoft tenant every personal Microsoft account (outlook.com, hotmail.com, ...) belongs to. */
export const MICROSOFT_CONSUMERS_TENANT = '9188040d-6c67-4c5b-b112-36a304b66dad';

/** Microsoft tenant settings besides one tenant id or domain. */
export const MICROSOFT_TENANT_MODES = ['common', 'organizations', 'consumers'] as const;

/** Stable refusal codes the hub puts in /#signin-error=<code>. */
export const SIGN_IN_ERROR_CODES = [
  'not-configured',
  'state',
  'busy',
  'denied',
  'unreachable',
  'idp-error',
  'token-invalid',
  'email-unverified',
  'domain-not-allowed',
  'tenant-not-allowed',
  'not-allowed',
  'already-member',
  'reverify',
  'invite-invalid',
  'invite-mismatch',
  'link-conflict',
  'link-expired',
  // NET-9: the page reached the hub over plain HTTP from another computer.
  'insecure',
] as const;
export type SignInErrorCode = (typeof SIGN_IN_ERROR_CODES)[number];

export function isSignInProvider(v: unknown): v is SignInProviderId {
  return typeof v === 'string' && (SIGN_IN_PROVIDERS as readonly string[]).includes(v);
}

export function isSignInErrorCode(v: unknown): v is SignInErrorCode {
  return typeof v === 'string' && (SIGN_IN_ERROR_CODES as readonly string[]).includes(v);
}

/** One sign-in button. */
export interface SignInProvider {
  id: SignInProviderId;
  label: string;
  /** Same-origin GET path for a plain sign-in; the client appends ?next=. Never carries an invite. */
  startUrl: string;
  /** For example 'Work, school or personal Microsoft account'. */
  hint?: string;
}

export interface Member {
  id: Id;
  /**
   * GitHub login, or 'owner' for the bootstrap owner link. For Google and Microsoft members:
   * a proven email, else 'google:<sub>' / 'microsoft:<tid>:<oid>'. Never
   * changes after the member is created.
   */
  login: string;
  name: string | null;
  avatarUrl: string | null;
  role: Role;
  /** How the member was created. Linking another sign-in later never changes it. */
  provider: SignInProviderId | 'owner-link';
  createdAt: Ts;
  lastSeenAt: Ts | null;
  /**
   * Why this person may sign in: 'user' (named on the list), 'org:<name>', 'invite',
   * 'domain:<d>', 'tenant:<tid>' or 'link' (created by linking from the owner link).
   */
  via?: string;
  /** The latest email the provider asserted, for display. */
  email?: string | null;
  /** False: the provider did not say the address was verified (the Members badge). */
  emailVerified?: boolean;
}

/** A Google or Microsoft account attached to a member. */
export interface LinkedIdentity {
  id: Id;
  memberId: Id;
  provider: OidcProviderId;
  email: string | null;
  emailVerified: boolean;
  emailAuthoritative: boolean;
  domain: string | null;
  tenantId: string | null;
  accountType: 'workspace' | 'personal' | 'work' | 'guest' | null;
  /** How it was attached; 'invite' shows "Added by <addedBy> through an invite" to the member. */
  addedVia: 'created' | 'self' | 'invite' | 'owner-link' | 'join';
  addedBy: string;
  linkedAt: Ts;
  lastUsedAt: Ts | null;
  /** The identity that admitted a domain or tenant member. The member cannot unlink it. */
  anchor: boolean;
  /**
   * addedVia 'invite' and the member has not confirmed it yet: it signs in only while
   * addedBy may still manage the member. The member confirms it (POST
   * /api/auth/identities/:id/confirm) or unlinks it. Absent from older hubs.
   */
  awaitingConfirm?: boolean;
  /** Unconfirmed, and addedBy was removed or demoted: it does not sign in until confirmed. */
  suspended?: boolean;
}

export interface InviteInfo {
  id: Id;
  kind: 'new' | 'link';
  /** Typed by the admin; required for both kinds. */
  email: string;
  /** 'link': the member the sign-in is added to. */
  memberId: Id | null;
  targetLogin: string | null;
  /** 'new': the role as created. 'link': the target's role. */
  role: Role;
  /** What accepting would grant now: the lower of role and the creator's current role. Null: the creator is gone and the invite is dead. */
  effectiveRole: Role | null;
  createdBy: string;
  createdAt: Ts;
  expiresAt: Ts;
  usedAt: Ts | null;
  usedBy: string | null;
  status: 'pending' | 'used' | 'expired' | 'revoked';
  revokedReason?: 'admin' | 'creator-removed' | 'member-removed' | null;
}

/** POST /api/auth/invites. hours 1..168, default 72; at most 72 for admin and owner roles. */
export interface CreateInviteRequest {
  email: string;
  role: Role;
  hours?: number;
}

/** POST /api/auth/invites/link. hours at most 72; at most 24 when the target is an admin or owner. */
export interface CreateLinkInviteRequest {
  memberId: Id;
  email: string;
  hours?: number;
}

/** The secret is only in `url` (a fragment, `<origin>/#invite=<secret>`) and is shown once; the hub keeps its sha256. */
export interface CreateInviteResponse {
  info: InviteInfo;
  url: string;
}

/** POST /api/auth/invites/peek { code } (no sign-in needed). */
export interface InvitePeek {
  valid: boolean;
  kind: 'new' | 'link' | null;
  /** For example 'a***@acme.com'. */
  emailHint: string | null;
  /** The effective role. */
  role: Role | null;
  /** 'link': which member the sign-in is added to. */
  targetLogin: string | null;
  expiresAt: Ts | null;
  /** Providers able to redeem it now. For 'link', only those that can prove an email. */
  providers: SignInProvider[];
  /**
   * 'new': the role is admin or owner, so the provider has to prove the account owns the
   * invited address. Otherwise the address is checked as asserted (not proof) and becomes
   * the login only when proven. Absent from older hubs.
   */
  provenEmailNeeded?: boolean;
  /** Set when this page's origin differs from the providers' callback origin: open the invite there. */
  openAt: string | null;
}

/** POST /api/auth/<provider>/start { invite, next } and POST /api/auth/identities/link { provider, next }. */
export interface StartFlowResponse {
  url: string;
}

/** Who joins without an invite: a Google Workspace domain or a Microsoft tenant. Off by default. */
export interface JoinRule {
  id: Id;
  provider: OidcProviderId;
  kind: 'domain' | 'tenant';
  value: string;
  role: 'viewer' | 'operator';
  source: 'config' | 'settings';
  addedBy: string | null;
  addedAt: Ts | null;
}

/** POST /api/auth/join-rules */
export interface CreateJoinRuleRequest {
  provider: OidcProviderId;
  kind: 'domain' | 'tenant';
  value: string;
  role: 'viewer' | 'operator';
}

/** A removed person whose Google or Microsoft accounts may not rejoin until allowed again. */
export interface RemovedPerson {
  id: Id;
  login: string;
  email: string | null;
  providers: OidcProviderId[];
  /** Role at removal. */
  role: Role;
  removedAt: Ts;
  removedBy: string;
  blocked: boolean;
}

export interface ProviderStatus {
  id: SignInProviderId;
  label: string;
  configured: boolean;
  needsAttention: boolean;
  /** Admins only; [] for others. */
  problems: string[];
  /** Admins only; '' for others. */
  redirectUri: string;
  startUrl: string;
  /** Admins only. */
  clientId: string | null;
  /** A loopback test identity provider is configured. */
  testIssuer: boolean;
  google?: { allowedDomains: Array<{ value: string; source: 'config' | 'settings' }> };
  microsoft?: {
    tenant: string;
    audience: 'single' | 'common' | 'organizations' | 'consumers';
    /** Single tenant given by domain: the tenant id read from Microsoft's discovery document. */
    pinnedTenantId: string | null;
    allowedTenants: Array<{ value: string; source: 'config' | 'settings' }>;
    trustedEmailDomains: Array<{ tenantId: string; domains: string[]; source: 'config' | 'settings' }>;
  };
}

/** GET /api/auth/sign-in */
export interface SignInSettings {
  providers: ProviderStatus[];
  joinRules: JoinRule[];
  /** Admins; [] for others. */
  invites: InviteInfo[];
  /** Admins: everyone; others: themselves. Keyed by member id. */
  identities: Record<Id, LinkedIdentity[]>;
  /** Admins; [] for others. */
  removed: RemovedPerson[];
  /** '' for non-admins. */
  configFile: string;
  canInvite: boolean;
  /** Owners: who may use Google and Microsoft, and join rules. */
  canConfigure: boolean;
}

/** GET /api/auth/identities/mine */
export interface MyIdentities {
  identities: LinkedIdentity[];
  canLink: SignInProvider[];
}

/** PUT /api/auth/sign-in/policy: the Settings-edited lists (owner only). Effective lists are auth.json plus these. */
export interface SignInPolicyRequest {
  google?: { allowedDomains?: string[] };
  microsoft?: { allowedTenants?: string[]; trustedEmailDomains?: Record<string, string[]> };
}

/** PUT /api/auth/sign-in/policy?dryRun=1 */
export interface PolicyPreview {
  sessionsEnded: number;
  identitiesNoLongerAllowed: number;
}

/** An API token for the CLI, CI or an AI agent. Shown once, stored hashed, always expires. */
export interface ApiTokenInfo {
  id: Id;
  name: string;
  /** 'agent' tokens act as an AI agent: actor kind 'agent', audited as agent:<name>. */
  kind: 'personal' | 'agent';
  role: Role;
  /** Narrower capabilities, e.g. ['read', 'session.start']. Empty: everything the role allows. */
  scopes: string[];
  createdBy: string;
  createdAt: Ts;
  expiresAt: Ts;
  lastUsedAt: Ts | null;
}

/** Who may sign in with GitHub, and with which role. */
export interface AccessRule {
  kind: 'user' | 'org';
  /** GitHub login or organisation, compared without case. */
  name: string;
  role: Role;
  /** 'config' rules come from <home>/hub/auth.json and are edited there; 'settings' rules from the UI. */
  source: 'config' | 'settings';
  addedBy: string | null;
  addedAt: Ts | null;
}

/** GET /api/auth/providers (no sign-in needed): what the sign-in screen offers. */
export interface AuthProviders {
  github: { configured: boolean };
  /** The owner link printed by the hub always works. */
  ownerLink: true;
  /** Every configured sign-in method, in button order. Optional: older hubs do not send it. */
  list?: SignInProvider[];
  /** True when a sign-in nobody knows creates a new workspace it owns (ADR 0080); hubs send none. */
  signup?: boolean;
  /** 'control-plane' on the hosted control plane (apps/control-plane); a hub sends none. */
  server?: 'hub' | 'control-plane';
}

/** GET /api/me */
export interface Me {
  /** Who the hub audits this caller as. */
  actor: { id: string; kind: 'human' | 'agent' | 'system'; role: Role };
  /** The person, or null for an agent token. */
  member: Member | null;
  via: 'owner-link' | 'token' | SignInProviderId;
  /** The token in use, for bearer requests. */
  token: ApiTokenInfo | null;
  scopes: string[];
  /** Which sign-in methods this hub offers. */
  providers: { github: boolean; list?: SignInProvider[] };
}

/** GET /api/auth/members: admins see everyone and the rules; others see only themselves. */
export interface MembersResponse {
  members: Member[];
  rules: AccessRule[];
  github: { configured: boolean; configFile: string; callbackUrl: string };
  canManage: boolean;
}

/** GET /api/auth/tokens: admins see every token; others see the ones they created. */
export interface TokensResponse {
  tokens: ApiTokenInfo[];
  canManage: boolean;
}

/** POST /api/auth/tokens */
export interface CreateTokenRequest {
  name: string;
  kind: 'personal' | 'agent';
  role: Role;
  scopes?: string[];
  /** 1 to TOKEN_MAX_DAYS; default TOKEN_MAX_DAYS. */
  days?: number;
}

/** The secret is in `token` and is shown once; only its hash is kept. */
export interface CreateTokenResponse {
  token: string;
  info: ApiTokenInfo;
}

/** POST /api/auth/device: the hub starts GitHub's device flow for `orgus login`. */
export interface DeviceStartResponse {
  handle: string;
  userCode: string;
  verificationUri: string;
  /** Seconds between polls. */
  interval: number;
  expiresIn: number;
}

/** POST /api/auth/device/poll { handle } */
export type DevicePollResponse =
  | { status: 'pending' | 'slow_down'; interval: number }
  | { status: 'ok'; token: string; info: ApiTokenInfo; member: Member }
  | { status: 'denied' | 'expired'; message: string };

/**
 * state.ext.auth. The state goes to every signed-in browser, so it carries nothing private:
 * members and tokens are read through GET /api/auth/members and /api/auth/tokens, which
 * check the caller's role. `rev` changes whenever either list changes (SSE 'ext' auth
 * 'changed' carries the new rev).
 */
export interface AuthState {
  github: { configured: boolean };
  rev: number;
}
